import { supabase } from '../../../config/supabase';
import type { AssessmentSessionV2 } from '../types';

export class AssessmentSessionsV2Service {
  private static mapRecord(item: AssessmentSessionV2): AssessmentSessionV2 {
    return {
      ...item,
      selectedTests: Array.isArray(item.selectedTests) ? item.selectedTests : [],
      status: item.status ?? 'completed',
    };
  }

  static async countByClientId(clientId: string): Promise<number> {
    const { count, error } = await supabase
      .from('assessment_sessions_v2')
      .select('sessionId', { count: 'exact', head: true })
      .eq('clientId', clientId);

    if (error) {
      console.error('Erro ao contar assessment_sessions_v2:', error);
      throw error;
    }

    return count ?? 0;
  }

  static async listRecentByClientId(clientId: string, limit = 5): Promise<AssessmentSessionV2[]> {
    const { data, error } = await supabase
      .from('assessment_sessions_v2')
      .select('*')
      .eq('clientId', clientId)
      .order('sessionDate', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Erro ao listar assessment_sessions_v2:', error);
      throw error;
    }

    return ((data as AssessmentSessionV2[] | null) ?? []).map((item) => this.mapRecord(item));
  }

  static async listByStatus(
    clientId: string,
    status: AssessmentSessionV2['status'],
    limit = 20,
  ): Promise<AssessmentSessionV2[]> {
    const { data, error } = await supabase
      .from('assessment_sessions_v2')
      .select('*')
      .eq('clientId', clientId)
      .eq('status', status)
      .order('updatedAt', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Erro ao listar assessment_sessions_v2 por status:', error);
      throw error;
    }

    return ((data as AssessmentSessionV2[] | null) ?? []).map((item) => this.mapRecord(item));
  }

  static async getById(sessionId: string, clientId: string): Promise<AssessmentSessionV2> {
    const { data, error } = await supabase
      .from('assessment_sessions_v2')
      .select('*')
      .eq('sessionId', sessionId)
      .eq('clientId', clientId)
      .single();

    if (error) {
      console.error('Erro ao buscar assessment_sessions_v2 por id:', error);
      throw error;
    }

    return this.mapRecord(data as AssessmentSessionV2);
  }

  static async create(
    payload: Omit<AssessmentSessionV2, 'sessionId' | 'createdAt' | 'updatedAt'>,
  ): Promise<AssessmentSessionV2> {
    const { data, error } = await supabase
      .from('assessment_sessions_v2')
      .insert({
        ...payload,
        selectedTests: Array.isArray(payload.selectedTests) ? payload.selectedTests : [],
      })
      .select('*')
      .single();

    if (error) {
      console.error('Erro ao criar assessment_sessions_v2:', error);
      throw error;
    }

    return this.mapRecord(data as AssessmentSessionV2);
  }

  static async delete(sessionId: string, clientId: string): Promise<void> {
    const { error } = await supabase
      .from('assessment_sessions_v2')
      .delete()
      .eq('sessionId', sessionId)
      .eq('clientId', clientId);

    if (error) {
      console.error('Erro ao excluir assessment_sessions_v2:', error);
      throw error;
    }
  }

  static async updateStatus(
    sessionId: string,
    clientId: string,
    status: AssessmentSessionV2['status'],
  ): Promise<AssessmentSessionV2> {
    return this.update(sessionId, clientId, { status });
  }

  static async update(
    sessionId: string,
    clientId: string,
    payload: Partial<Pick<AssessmentSessionV2, 'sessionDate' | 'notes' | 'selectedTests' | 'status'>>,
  ): Promise<AssessmentSessionV2> {
    const { data, error } = await supabase
      .from('assessment_sessions_v2')
      .update({
        ...payload,
        ...(payload.selectedTests ? { selectedTests: Array.isArray(payload.selectedTests) ? payload.selectedTests : [] } : {}),
        updatedAt: new Date().toISOString(),
      })
      .eq('sessionId', sessionId)
      .eq('clientId', clientId)
      .select('*')
      .single();

    if (error) {
      console.error('Erro ao atualizar status de assessment_sessions_v2:', error);
      throw error;
    }

    return this.mapRecord(data as AssessmentSessionV2);
  }
}
